import * as React from "react";
import { Loader2, Sparkles } from "lucide-react";
import { ui } from "@patient-watch/ui";
import { Progress } from "@patient-watch/ui/progress";
import { cn } from "@/lib/utils";

import { FORM_DRAFT_PROMPT_MAX_CHARS } from "./formDraft";

const DRAFT_PROGRESS_STEPS = ["Prompt", "Draft", "Check"] as const;
const DRAFT_PROGRESS_MESSAGES = ["Reading your prompt.", "Building the draft.", "Checking the result."] as const;

function DraftWithAiProgress() {
    const [activeStep, setActiveStep] = React.useState(0);

    React.useEffect(() => {
        const draftTimer = window.setTimeout(() => setActiveStep(1), 1500);
        const checkTimer = window.setTimeout(() => setActiveStep(2), 3000);
        return () => {
            window.clearTimeout(draftTimer);
            window.clearTimeout(checkTimer);
        };
    }, []);

    return (
        <div className="space-y-3 rounded-lg border bg-muted/40 p-4" data-testid="draft-with-ai-progress">
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">Creating your draft</p>
            <p className="text-xs font-medium text-slate-900 dark:text-slate-100" aria-live="polite">
                {DRAFT_PROGRESS_MESSAGES[activeStep]}
            </p>
            <div className="flex items-center gap-3">
                {DRAFT_PROGRESS_STEPS.map((step, index) => {
                    const status = index < activeStep ? "done" : index === activeStep ? "running" : "pending";
                    return (
                        <Progress
                            key={step}
                            value={status === "done" ? 100 : status === "running" ? 55 : 0}
                            className={cn("h-3 flex-1", status === "running" && "animate-pulse")}
                            aria-label={`${step}: ${status}`}
                        />
                    );
                })}
            </div>
            <div className="flex gap-3 text-xs font-medium">
                {DRAFT_PROGRESS_STEPS.map((step, index) => (
                    <span
                        key={step}
                        className={cn(
                            "flex-1 text-left",
                            index < activeStep
                                ? "text-indigo-600 dark:text-indigo-400"
                                : index === activeStep
                                  ? "text-violet-700 dark:text-violet-300"
                                  : "text-muted-foreground"
                        )}
                    >
                        {step}
                    </span>
                ))}
            </div>
            <p className="text-xs text-muted-foreground">Please keep this dialog open while we finish.</p>
        </div>
    );
}

export interface DraftWithAiDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    description: string;
    placeholder: string;
    prompt: string;
    onPromptChange: (value: string) => void;
    promptMaxChars?: number;
    generateLabel?: string;
    retryLabel?: string;
    confirmLabel?: string;
    discardLabel?: string;
    isGenerating?: boolean;
    isConfirming?: boolean;
    confirmDisabled?: boolean;
    error?: string | null;
    warnings?: string[];
    hasPreview: boolean;
    onGenerate: () => void;
    onConfirm: () => void;
    onDiscard: () => void;
    children?: React.ReactNode;
}

export function DraftWithAiDialog({
    open,
    onOpenChange,
    title,
    description,
    placeholder,
    prompt,
    onPromptChange,
    promptMaxChars = FORM_DRAFT_PROMPT_MAX_CHARS,
    generateLabel = "Generate draft",
    retryLabel = "Retry",
    confirmLabel = "Use this draft",
    discardLabel = "Discard",
    isGenerating = false,
    isConfirming = false,
    confirmDisabled = false,
    error,
    warnings = [],
    hasPreview,
    onGenerate,
    onConfirm,
    onDiscard,
    children,
}: DraftWithAiDialogProps) {
    const remaining = promptMaxChars - prompt.length;
    const canGenerate = prompt.trim().length > 0 && !isGenerating && !isConfirming;

    const handleOpenChange = (nextOpen: boolean) => {
        if (!nextOpen && isConfirming) return;
        if (!nextOpen) onDiscard();
        onOpenChange(nextOpen);
    };

    return (
        <ui.Dialog open={open} onOpenChange={handleOpenChange}>
            <ui.DialogContent className="flex max-h-[90vh] max-w-4xl flex-col gap-4 overflow-hidden">
                <ui.DialogHeader>
                    <ui.DialogTitle className="flex items-center gap-2">
                        <Sparkles className="h-4 w-4" aria-hidden />
                        {title}
                    </ui.DialogTitle>
                    <ui.DialogDescription>{description}</ui.DialogDescription>
                </ui.DialogHeader>

                <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
                    <div className="space-y-1.5">
                        <label htmlFor="draft-with-ai-prompt" className="text-sm font-medium">
                            Prompt
                        </label>
                        <ui.Textarea
                            id="draft-with-ai-prompt"
                            data-testid="draft-with-ai-prompt"
                            value={prompt}
                            maxLength={promptMaxChars}
                            placeholder={placeholder}
                            onChange={(event) => onPromptChange(event.target.value)}
                            disabled={isGenerating || isConfirming}
                            className={hasPreview ? "min-h-16" : "min-h-28"}
                        />
                        <p className="text-2xs text-muted-foreground">
                            {hasPreview
                                ? "Edit the prompt and retry if this draft is not right."
                                : `${remaining} characters left`}
                        </p>
                    </div>

                    {isGenerating ? <DraftWithAiProgress /> : null}

                    {error ? (
                        <p className="text-sm text-destructive" role="alert" data-testid="draft-with-ai-error">
                            {error}
                        </p>
                    ) : null}

                    {warnings.length > 0 ? (
                        <ul className="space-y-1 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                            {warnings.map((warning) => (
                                <li key={warning}>{warning}</li>
                            ))}
                        </ul>
                    ) : null}

                    {hasPreview && !isGenerating ? (
                        <div data-testid="draft-with-ai-preview" className="space-y-2">
                            {children ?? (
                                <p className="rounded-lg border bg-muted/20 p-4 text-sm">
                                    The draft is ready. Continue editing to open it.
                                </p>
                            )}
                        </div>
                    ) : null}
                </div>

                <ui.DialogFooter className="flex-row flex-nowrap items-center justify-between gap-2 sm:flex-row sm:justify-between">
                    <ui.Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        data-testid="draft-with-ai-discard"
                        onClick={() => handleOpenChange(false)}
                        disabled={isGenerating || isConfirming}
                    >
                        {discardLabel}
                    </ui.Button>
                    <div className="flex shrink-0 flex-nowrap items-center justify-end gap-2">
                        <ui.Button
                            type="button"
                            size="sm"
                            variant="outline"
                            data-testid="draft-with-ai-generate"
                            onClick={onGenerate}
                            disabled={!canGenerate}
                        >
                            {isGenerating ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <Sparkles className="h-4 w-4" />
                            )}
                            {hasPreview ? retryLabel : generateLabel}
                        </ui.Button>
                        {hasPreview ? (
                            <ui.Button
                                type="button"
                                size="sm"
                                data-testid="draft-with-ai-confirm"
                                onClick={onConfirm}
                                disabled={isGenerating || isConfirming || confirmDisabled}
                            >
                                {isConfirming ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                {confirmLabel}
                            </ui.Button>
                        ) : null}
                    </div>
                </ui.DialogFooter>
            </ui.DialogContent>
        </ui.Dialog>
    );
}
